import React, { Fragment, useState } from "react";
import { Box, Link, styled, useTheme, IconButton, Drawer, List, ListItem, ListItemText, AppBar, Toolbar, Container } from '@mui/material';
import { useNavigate } from "react-router-dom";
import CommonButton from "../../common/components/CommonButton";
import { East, Menu as MenuIcon, Close } from "@mui/icons-material";
import CommonBookSession from '../../common/components/CommonBookSession';

const Links = styled(Link)({
    textDecoration: 'none',
    color: 'black',
    fontSize: 16,
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'color 0.3s ease',
    whiteSpace: 'nowrap',
    '&:hover': {
        color: '#1976d2',
    }
});

const NavLinksContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    [theme.breakpoints.down('md')]: {
        display: 'none',
    },
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.down('md')]: {
        display: 'flex',
    },
}));

const wrapper = (children, fromLanding) => {
    return fromLanding ? (
        <Fragment>
            {children}
        </Fragment>
    ) : (
        <Box sx={{
            pl: { xs: 2, sm: 3, md: 4 },
            pr: { xs: 2, sm: 3, md: 4 }
        }}>
            {children}
        </Box>
    )
};

const Navbar = ({
    handleLinkClick,
    homeRef,
    aboutRef,
    blogsRef,
    featureRef,
    testimonialRef,
    fromLanding
}) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleNavClick = (e, ref, path) => {
        if (fromLanding && ref) {
            handleLinkClick(e, ref);
        }
        navigate(path);
        setMobileMenuOpen(false);
    };

    const navItems = [
        { label: 'Home', ref: homeRef, path: '/', href: '#home' },
        { label: 'About Us', ref: aboutRef, path: '/', href: '#about' },
        { label: 'College Planner', ref: featureRef, path: '/', href: '#college-planner' },
        { label: 'Blogs', ref: blogsRef, path: '/', href: '#blogs' },
        { label: 'Testimonials', ref: testimonialRef, path: '/', href: '#testimonial' },
    ];

    return (
        <Fragment>
            <CommonBookSession
                bookingUrl='https://calendly.com/bixjet-dreamcollegepath/30min'
                open={open}
                setOpen={setOpen}
            />
            {wrapper(
                <AppBar
                    position="sticky"
                    color="default"
                    elevation={0}
                    sx={{
                        backgroundColor: 'white',
                        borderRadius: 1,
                        marginTop: '20px',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
                    }}
                >
                    <Container maxWidth="xl">
                        <Toolbar disableGutters sx={{ minHeight: { xs: 70, md: 80 } }}>
                            {/* Logo */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexShrink: 0,
                                mr: 2, // Add margin right
                                cursor: 'pointer'
                            }}
                                onClick={() => navigate('/')}
                            >
                                <img
                                    src='/Images/landingPagelogo.png'
                                    alt='logo'
                                    style={{
                                        maxWidth: '100%',
                                        height: 'auto',
                                        width: 'auto',
                                        maxHeight: '50px'
                                    }}
                                />
                            </Box>

                            {/* Desktop Navigation */}
                            <NavLinksContainer sx={{ flexGrow: 1 }}>
                                {navItems.map((item) => (
                                    <Links
                                        key={item.label}
                                        href={item.href}
                                        onClick={(e) => handleNavClick(e, item.ref, item.path)}
                                        sx={{
                                            fontSize: { lg: 16, xl: 17 }
                                        }}
                                    >
                                        {item.label}
                                    </Links>
                                ))}
                            </NavLinksContainer>

                            {/* Desktop Action Buttons */}
                            <Box sx={{
                                display: { xs: 'none', md: 'flex' },
                                alignItems: 'center',
                                gap: 1.5, // Reduce gap between buttons
                                flexShrink: 0
                            }}>
                                <CommonButton
                                    label="Book DCP Planner Demo"
                                    endIcon={<East />}
                                    fontWeight={600}
                                    width="auto" // Auto width based on content
                                    minWidth="200px" // Minimum width to ensure text fits
                                    height="45px"
                                    fontSize={{ lg: '15px', xl: '16px' }}
                                    sx={{
                                        whiteSpace: 'nowrap', // Keep text in one line
                                        padding: '6px 16px',
                                    }}
                                    handleClick={() => setOpen(true)}
                                    marginBottom="0px"
                                />
                                <CommonButton
                                    label="Login/Sign Up"
                                    fontWeight={600}
                                    width="auto" // Auto width based on content
                                    minWidth="130px" // Minimum width
                                    height="45px"
                                    color={theme.palette.primary.main}
                                    background={theme.palette.secondary.main}
                                    fontSize={{ lg: '15px', xl: '16px' }}
                                    sx={{
                                        whiteSpace: 'nowrap', // Keep text in one line
                                        padding: '6px 16px',
                                    }}
                                    marginBottom="0px"
                                    handleClick={() => navigate('/login')}
                                />
                            </Box>

                            {/* Mobile Menu Button */}
                            <MobileMenuButton
                                edge="end"
                                color="inherit"
                                aria-label="menu"
                                onClick={() => setMobileMenuOpen(true)}
                                sx={{ ml: 'auto' }}
                            >
                                <MenuIcon />
                            </MobileMenuButton>

                            {/* Mobile Drawer */}
                            <Drawer
                                anchor="right"
                                open={mobileMenuOpen}
                                onClose={() => setMobileMenuOpen(false)}
                                PaperProps={{
                                    sx: {
                                        width: { xs: '280px', sm: '320px' },
                                        backgroundColor: 'white'
                                    }
                                }}
                            >
                                <Box sx={{ p: 2 }}>
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        mb: 2
                                    }}>
                                        <IconButton onClick={() => setMobileMenuOpen(false)}>
                                            <Close />
                                        </IconButton>
                                    </Box>

                                    {/* Mobile Logo */}
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        mb: 3
                                    }}>
                                        <img
                                            src='/Images/landingPagelogo.png'
                                            alt='logo'
                                            style={{
                                                maxWidth: '150px',
                                                height: 'auto'
                                            }}
                                        />
                                    </Box>

                                    {/* Mobile Navigation Links */}
                                    <List>
                                        {navItems.map((item) => (
                                            <ListItem
                                                key={item.label}
                                                disablePadding
                                                sx={{ mb: 1 }}
                                            >
                                                <ListItemText>
                                                    <Links
                                                        href={item.href}
                                                        onClick={(e) => handleNavClick(e, item.ref, item.path)}
                                                        sx={{
                                                            display: 'block',
                                                            padding: '10px 16px',
                                                            fontSize: '16px',
                                                            borderRadius: '8px',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(25, 118, 210, 0.08)'
                                                            }
                                                        }}
                                                    >
                                                        {item.label}
                                                    </Links>
                                                </ListItemText>
                                            </ListItem>
                                        ))}
                                    </List>

                                    {/* Mobile Action Buttons */}
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        mt: 3,
                                        px: 1
                                    }}>
                                        <CommonButton
                                            label="Book DCP Planner Demo"
                                            endIcon={<East />}
                                            fontWeight={600}
                                            width="100%"
                                            height="47px"
                                            fontSize="16px"
                                            sx={{
                                                whiteSpace: 'nowrap',
                                            }}
                                            handleClick={() => {
                                                setOpen(true);
                                                setMobileMenuOpen(false);
                                            }}
                                            marginBottom="0px"
                                        />
                                        <CommonButton
                                            label="Login/Sign Up"
                                            fontWeight={600}
                                            width="100%"
                                            height="47px"
                                            color={theme.palette.primary.main}
                                            background={theme.palette.secondary.main}
                                            fontSize="16px"
                                            marginBottom="0px"
                                            sx={{
                                                whiteSpace: 'nowrap',
                                            }}
                                            handleClick={() => {
                                                navigate('/login');
                                                setMobileMenuOpen(false);
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Drawer>
                        </Toolbar>
                    </Container>
                </AppBar>,
                fromLanding
            )}
        </Fragment>
    )
};

export default Navbar;